/**
 * Indicator Registry — all supported indicators for the community dashboard.
 *
 * Each entry is the technical bridge between the live APIs (which provide labels)
 * and the data hooks (which need variable codes). The registry is the source of
 * truth for which indicators are "supported"; useCatalog.js merges in live labels.
 *
 * Census entries use ACS 5-year estimates.
 * CDC entries all share viewId 'swc5-untb' (CDC PLACES county dataset).
 *
 * Target: ~60 indicators — percent or dollar values wherever possible,
 * covering demographics, economic security, housing, education, employment,
 * family, language, health, and transportation.
 */

export const INDICATOR_REGISTRY = [

  // ── Demographics ─────────────────────────────────────────────────────────────

  {
    id: 'median_age',
    label: 'Median Age',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B01002',
    numeratorVar: 'B01002_001E',
    denominatorVar: null,
    unit: 'count',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'population_under_5',
    label: 'Population Under 5',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B01001',
    numeratorVar: 'B01001_003E+B01001_027E',
    denominatorVar: 'B01001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'population_under_18',
    label: 'Population Under 18',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B01001',
    numeratorVar: 'B01001_003E+B01001_004E+B01001_005E+B01001_006E+B01001_027E+B01001_028E+B01001_029E+B01001_030E',
    denominatorVar: 'B01001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'population_65_plus',
    label: 'Population 65 and Over',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B01001',
    numeratorVar: 'B01001_020E+B01001_021E+B01001_022E+B01001_023E+B01001_024E+B01001_025E+B01001_044E+B01001_045E+B01001_046E+B01001_047E+B01001_048E+B01001_049E',
    denominatorVar: 'B01001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'hispanic_or_latino',
    label: 'Hispanic or Latino',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B03003',
    numeratorVar: 'B03003_003E',
    denominatorVar: 'B03003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'black_or_african_american',
    label: 'Black or African American',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B02001',
    numeratorVar: 'B02001_003E',
    denominatorVar: 'B02001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'native_american',
    label: 'American Indian or Alaska Native',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B02001',
    numeratorVar: 'B02001_004E',
    denominatorVar: 'B02001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'asian_alone',
    label: 'Asian Alone',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B02001',
    numeratorVar: 'B02001_005E',
    denominatorVar: 'B02001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'two_or_more_races',
    label: 'Two or More Races',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B02001',
    numeratorVar: 'B02001_008E',
    denominatorVar: 'B02001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'foreign_born',
    label: 'Foreign-Born Population',
    topic: 'Demographics',
    source: 'census',
    censusTable: 'B05002',
    numeratorVar: 'B05002_013E',
    denominatorVar: 'B05002_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Income & Poverty ─────────────────────────────────────────────────────────

  {
    id: 'poverty_rate',
    label: 'Poverty Rate',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B17001',
    numeratorVar: 'B17001_002E',
    denominatorVar: 'B17001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'child_poverty_rate',
    label: 'Child Poverty Rate',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B17006',
    numeratorVar: 'B17006_002E',
    denominatorVar: 'B17006_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'median_household_income',
    label: 'Median Household Income',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B19013',
    numeratorVar: 'B19013_001E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'median_family_income',
    label: 'Median Family Income',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B19113',
    numeratorVar: 'B19113_001E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'per_capita_income',
    label: 'Per Capita Income',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B19301',
    numeratorVar: 'B19301_001E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'gini_index',
    label: 'Gini Index of Income Inequality',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B19083',
    numeratorVar: 'B19083_001E',
    denominatorVar: null,
    unit: 'count',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'income_under_25k',
    label: 'Households with Income Under $25,000',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B19001',
    numeratorVar: 'B19001_002E+B19001_003E+B19001_004E+B19001_005E',
    denominatorVar: 'B19001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'snap_households',
    label: 'SNAP Receipt',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B22003',
    numeratorVar: 'B22003_002E',
    denominatorVar: 'B22003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'public_assistance_income',
    label: 'Households with Public Assistance Income',
    topic: 'Income & Poverty',
    source: 'census',
    censusTable: 'B19057',
    numeratorVar: 'B19057_002E',
    denominatorVar: 'B19057_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Housing ──────────────────────────────────────────────────────────────────

  {
    id: 'housing_cost_burden',
    label: 'Renter Cost Burden (30%+ of Income)',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25070',
    numeratorVar: 'B25070_007E+B25070_008E+B25070_009E+B25070_010E',
    denominatorVar: 'B25070_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'severe_rent_burden',
    label: 'Severe Renter Cost Burden (50%+ of Income)',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25070',
    numeratorVar: 'B25070_010E',
    denominatorVar: 'B25070_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'owner_cost_burden',
    label: 'Owner Cost Burden (30%+ of Income)',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25091',
    numeratorVar: 'B25091_008E+B25091_009E+B25091_010E+B25091_011E+B25091_019E+B25091_020E+B25091_021E+B25091_022E',
    denominatorVar: 'B25091_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'median_gross_rent',
    label: 'Median Gross Rent',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25064',
    numeratorVar: 'B25064_001E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'median_monthly_owner_costs',
    label: 'Median Monthly Owner Costs (With Mortgage)',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25088',
    numeratorVar: 'B25088_002E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'median_home_value',
    label: 'Median Home Value',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25077',
    numeratorVar: 'B25077_001E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'owner_occupied',
    label: 'Owner-Occupied Housing',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25003',
    numeratorVar: 'B25003_002E',
    denominatorVar: 'B25003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'vacant_housing',
    label: 'Vacant Housing Units',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25002',
    numeratorVar: 'B25002_003E',
    denominatorVar: 'B25002_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'housing_overcrowding',
    label: 'Overcrowded Housing Units',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25014',
    numeratorVar: 'B25014_005E+B25014_006E+B25014_007E+B25014_011E+B25014_012E+B25014_013E',
    denominatorVar: 'B25014_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'lacking_plumbing',
    label: 'Lacking Complete Plumbing Facilities',
    topic: 'Housing',
    source: 'census',
    censusTable: 'B25047',
    numeratorVar: 'B25047_003E',
    denominatorVar: 'B25047_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Health ───────────────────────────────────────────────────────────────────

  {
    id: 'no_health_insurance',
    label: 'Uninsured Rate',
    topic: 'Health',
    source: 'census',
    censusTable: 'B27003',
    numeratorVar: 'B27003_004E+B27003_007E+B27003_010E+B27003_013E+B27003_016E+B27003_019E+B27003_022E+B27003_025E+B27003_028E+B27003_031E+B27003_034E',
    denominatorVar: 'B27003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'disability_rate',
    label: 'Population with a Disability',
    topic: 'Health',
    source: 'census',
    censusTable: 'B18101',
    numeratorVar: 'B18101_004E+B18101_007E+B18101_010E+B18101_013E+B18101_016E+B18101_019E+B18101_023E+B18101_026E+B18101_029E+B18101_032E+B18101_035E+B18101_038E',
    denominatorVar: 'B18101_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Education ────────────────────────────────────────────────────────────────

  {
    id: 'less_than_high_school',
    label: 'Less Than High School Diploma',
    topic: 'Education',
    source: 'census',
    censusTable: 'B15003',
    numeratorVar: 'B15003_002E+B15003_003E+B15003_004E+B15003_005E+B15003_006E+B15003_007E+B15003_008E+B15003_009E+B15003_010E+B15003_011E+B15003_012E+B15003_013E+B15003_014E+B15003_015E+B15003_016E',
    denominatorVar: 'B15003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'bachelor_or_higher',
    label: "Bachelor's Degree or Higher",
    topic: 'Education',
    source: 'census',
    censusTable: 'B15003',
    numeratorVar: 'B15003_022E+B15003_023E+B15003_024E+B15003_025E',
    denominatorVar: 'B15003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'graduate_degree',
    label: "Graduate or Professional Degree",
    topic: 'Education',
    source: 'census',
    censusTable: 'B15003',
    numeratorVar: 'B15003_023E+B15003_024E+B15003_025E',
    denominatorVar: 'B15003_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'school_enrollment',
    label: 'School Enrollment (Age 3 and Over)',
    topic: 'Education',
    source: 'census',
    censusTable: 'B14001',
    numeratorVar: 'B14001_002E',
    denominatorVar: 'B14001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'college_enrollment',
    label: 'College Enrollment',
    topic: 'Education',
    source: 'census',
    censusTable: 'B14007',
    numeratorVar: 'B14007_003E',
    denominatorVar: 'B14007_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Employment ───────────────────────────────────────────────────────────────

  {
    id: 'unemployment_rate',
    label: 'Unemployment Rate',
    topic: 'Employment',
    source: 'census',
    censusTable: 'B23025',
    numeratorVar: 'B23025_005E',
    denominatorVar: 'B23025_002E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'labor_force_participation',
    label: 'Labor Force Participation Rate',
    topic: 'Employment',
    source: 'census',
    censusTable: 'B23025',
    numeratorVar: 'B23025_002E',
    denominatorVar: 'B23025_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'work_from_home',
    label: 'Works from Home',
    topic: 'Employment',
    source: 'census',
    censusTable: 'B08301',
    numeratorVar: 'B08301_021E',
    denominatorVar: 'B08301_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'commute_public_transit',
    label: 'Commute by Public Transit',
    topic: 'Employment',
    source: 'census',
    censusTable: 'B08301',
    numeratorVar: 'B08301_010E',
    denominatorVar: 'B08301_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'median_earnings',
    label: 'Median Earnings (Full-Time Workers)',
    topic: 'Employment',
    source: 'census',
    censusTable: 'B20002',
    numeratorVar: 'B20002_001E',
    denominatorVar: null,
    unit: 'dollars',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Transportation ───────────────────────────────────────────────────────────

  {
    id: 'vehicle_access',
    label: 'Households with No Vehicle',
    topic: 'Transportation',
    source: 'census',
    censusTable: 'B08201',
    numeratorVar: 'B08201_002E',
    denominatorVar: 'B08201_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Technology ───────────────────────────────────────────────────────────────

  {
    id: 'broadband_access',
    label: 'Broadband Internet Access',
    topic: 'Technology',
    source: 'census',
    censusTable: 'B28002',
    numeratorVar: 'B28002_004E',
    denominatorVar: 'B28002_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Family & Households ──────────────────────────────────────────────────────

  {
    id: 'households_with_children',
    label: 'Households with Children Under 18',
    topic: 'Family & Households',
    source: 'census',
    censusTable: 'B11002',
    numeratorVar: 'B11002_003E+B11002_005E+B11002_007E',
    denominatorVar: 'B11002_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'single_parent_households',
    label: 'Single-Parent Households',
    topic: 'Family & Households',
    source: 'census',
    censusTable: 'B11012',
    numeratorVar: 'B11012_004E+B11012_010E',
    denominatorVar: 'B11012_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    id: 'single_person_households',
    label: 'Single-Person Households',
    topic: 'Family & Households',
    source: 'census',
    censusTable: 'B11001',
    numeratorVar: 'B11001_008E',
    denominatorVar: 'B11001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Language ─────────────────────────────────────────────────────────────────

  {
    id: 'spanish_at_home',
    label: 'Spanish Speakers at Home',
    topic: 'Language',
    source: 'census',
    censusTable: 'B16001',
    numeratorVar: 'B16001_003E',
    denominatorVar: 'B16001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },
  {
    // Speaks English "less than very well" across all non-English language groups,
    // native and foreign-born. Source: B16005 (Nativity × Language × English ability)
    id: 'limited_english_proficiency',
    label: 'Limited English Proficiency',
    topic: 'Language',
    source: 'census',
    censusTable: 'B16005',
    numeratorVar: 'B16005_007E+B16005_008E+B16005_012E+B16005_013E+B16005_017E+B16005_018E+B16005_022E+B16005_023E+B16005_029E+B16005_030E+B16005_034E+B16005_035E+B16005_039E+B16005_040E+B16005_044E+B16005_045E',
    denominatorVar: 'B16005_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Veterans ─────────────────────────────────────────────────────────────────

  {
    id: 'veteran_population',
    label: 'Veteran Population',
    topic: 'Veterans',
    source: 'census',
    censusTable: 'B21001',
    numeratorVar: 'B21001_002E',
    denominatorVar: 'B21001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── Immigration ──────────────────────────────────────────────────────────────

  {
    id: 'non_citizen',
    label: 'Non-U.S. Citizens',
    topic: 'Immigration',
    source: 'census',
    censusTable: 'B05001',
    numeratorVar: 'B05001_006E',
    denominatorVar: 'B05001_001E',
    unit: 'percent',
    years: [2018, 2019, 2021, 2022, 2023, 2024]
  },

  // ── CDC PLACES — Health Outcomes ─────────────────────────────────────────────

  {
    id: 'diabetes',
    label: 'Diabetes',
    topic: 'Health Outcomes',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'DIABETES',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'obesity',
    label: 'Obesity',
    topic: 'Health Outcomes',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'OBESITY',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'high_blood_pressure',
    label: 'High Blood Pressure',
    topic: 'Health Outcomes',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'BPHIGH',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'high_cholesterol',
    label: 'High Cholesterol',
    topic: 'Health Outcomes',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'HIGHCHOL',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'mental_health',
    label: 'Poor Mental Health',
    topic: 'Health Outcomes',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'MHLTH',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'physical_health',
    label: 'Poor Physical Health',
    topic: 'Health Outcomes',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'PHLTH',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },

  // ── CDC PLACES — Risk Behaviors ──────────────────────────────────────────────

  {
    id: 'current_smoking',
    label: 'Current Smoking',
    topic: 'Risk Behaviors',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'CSMOKING',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'binge_drinking',
    label: 'Binge Drinking',
    topic: 'Risk Behaviors',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'BINGE',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },
  {
    id: 'no_physical_activity',
    label: 'No Physical Activity',
    topic: 'Risk Behaviors',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'LPA',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },

  // ── CDC PLACES — Prevention ──────────────────────────────────────────────────

  {
    id: 'annual_checkup',
    label: 'Annual Checkup',
    topic: 'Prevention',
    source: 'cdc',
    cdcViewId: 'swc5-untb',
    cdcMeasureId: 'CHECKUP',
    unit: 'percent',
    years: [2019, 2020, 2021, 2022, 2023, 2024]
  },

  // ── CDC/ATSDR Social Vulnerability Index ────────────────────────────────────

  {
    id: 'svi_overall',
    label: 'Social Vulnerability Index (Overall)',
    topic: 'Social Vulnerability',
    source: 'svi',
    sviField: 'RPL_THEMES',
    unit: 'percent',
    years: [2018, 2020, 2022]
  },
  {
    id: 'svi_socioeconomic',
    label: 'SVI — Socioeconomic',
    topic: 'Social Vulnerability',
    source: 'svi',
    sviField: 'RPL_THEME1',
    unit: 'percent',
    years: [2018, 2020, 2022]
  },
  {
    id: 'svi_household',
    label: 'SVI — Household Characteristics',
    topic: 'Social Vulnerability',
    source: 'svi',
    sviField: 'RPL_THEME2',
    unit: 'percent',
    years: [2018, 2020, 2022]
  },
  {
    id: 'svi_minority',
    label: 'SVI — Racial & Ethnic Minority',
    topic: 'Social Vulnerability',
    source: 'svi',
    sviField: 'RPL_THEME3',
    unit: 'percent',
    years: [2018, 2020, 2022]
  },
  {
    id: 'svi_housing_transport',
    label: 'SVI — Housing & Transportation',
    topic: 'Social Vulnerability',
    source: 'svi',
    sviField: 'RPL_THEME4',
    unit: 'percent',
    years: [2018, 2020, 2022]
  },

  // ── EPA EJSCREEN — Environmental Justice ────────────────────────────────────

  {
    id: 'ejscreen_cancer',
    label: 'Air Toxics Cancer Risk',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'CANCER',
    unit: 'index',
    years: [2024]
  },
  {
    id: 'ejscreen_resp',
    label: 'Respiratory Hazard Index',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'RESP',
    unit: 'index',
    years: [2024]
  },
  {
    id: 'ejscreen_pm25',
    label: 'PM2.5 Concentration (µg/m³)',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'PM25',
    unit: 'index',
    years: [2024]
  },
  {
    id: 'ejscreen_ozone',
    label: 'Ozone Concentration (ppb)',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'OZONE',
    unit: 'index',
    years: [2024]
  },
  {
    id: 'ejscreen_diesel',
    label: 'Diesel Particulate Matter',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'DSLPM',
    unit: 'index',
    years: [2024]
  },
  {
    id: 'ejscreen_traffic',
    label: 'Traffic Proximity',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'PTRAF',
    unit: 'index',
    years: [2024]
  },
  {
    id: 'ejscreen_lead',
    label: 'Lead Paint Indicator',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'PRE1960PCT',
    unit: 'percent',
    years: [2024]
  },
  {
    id: 'ejscreen_superfund',
    label: 'Superfund Proximity',
    topic: 'Environment',
    source: 'ejscreen',
    ejscreenField: 'PNPL',
    unit: 'index',
    years: [2024]
  },

  // ── Urban Institute / NCES Education Data ───────────────────────────────────

  {
    id: 'edu_graduation_rate',
    label: 'HS Graduation Rate (4-Year)',
    topic: 'Education',
    source: 'education',
    educationEndpoint: 'directory',
    educationField: 'grad_rate_adj_cohort_4yr',
    unit: 'percent',
    years: [2016, 2017, 2018, 2019, 2021, 2022]
  },
  {
    id: 'edu_enrollment',
    label: 'K-12 Enrollment',
    topic: 'Education',
    source: 'education',
    educationEndpoint: 'directory',
    educationField: 'enrollment',
    unit: 'count',
    years: [2016, 2017, 2018, 2019, 2021, 2022]
  },
  {
    id: 'edu_frl_pct',
    label: 'Free/Reduced Lunch Eligibility',
    topic: 'Education',
    source: 'education',
    educationEndpoint: 'directory',
    educationField: 'frl_pct',
    unit: 'percent',
    years: [2016, 2017, 2018, 2019, 2021, 2022]
  },
  {
    id: 'edu_expenditure_pupil',
    label: 'Per-Pupil Expenditure',
    topic: 'Education',
    source: 'education',
    educationEndpoint: 'finance',
    educationField: 'exp_total_per_pup',
    unit: 'dollars',
    years: [2016, 2017, 2018, 2019, 2021, 2022]
  }
];
